import React from 'react';

import * as analytics from 'analytics/Analytics';
import {WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import navigateToURL from 'lib/navigateToURL';
import {plainReferenceText, RelevantRowData} from 'relevant-items/shared/RelevantItemsTableUtils';
import BenchChip from 'ui-library/chips/BenchChip';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import APIUtil from 'util/APIUtil';

type FilesMentioningRowItem = BenchChipRowItem & {
  __typename?: string;
  parentId?: string | null;
};

interface BenchChipRowItem {
  id: string;
  name: string;
  modifiedAt: string | null;
  folder?: {source?: {name: string}} | null;
  parent?: {folder?: {source?: {name: string}} | null; __typename?: string} | null;
  source?: {name: string} | null;
}

interface TaskGroupRowItem {
  name: string;
  process: {
    modifiedAt: string | null;
    createdAt: string | null;
    editUrl: string | null;
    folder: {name: string} | null;
    processTaskSchema: {clientOrigin: string};
  };
}

export function getSupportedRelevantItems<TTagged, TFilesMentioning>(
  tagged: Array<TTagged>,
  filesMentioning: Array<TFilesMentioning>
): {
  supportedTagged: Array<TTagged>;
  supportedFilesMentioning: Array<TFilesMentioning>;
} {
  return {
    supportedTagged: tagged.filter(
      (item) => (item as {__typename?: string}).__typename !== 'EntryLikeDocumentNode'
    ),
    supportedFilesMentioning: filesMentioning.filter(
      (item) =>
        (item as {__typename?: string}).__typename !== 'EntryLikeDocumentNode' ||
        isWorksheetStep(item as {__typename?: string; parentId?: string | null})
    ),
  };
}

function getProjectFolderName(item: {
  id: string;
  folder?: {source?: {name: string}} | null;
  parent?: {folder?: {source?: {name: string}} | null; __typename?: string} | null;
  source?: {name: string} | null;
}): string | null {
  return item.folder?.source?.name ?? item.parent?.folder?.source?.name ?? item.source?.name ?? null;
}

export function isWorksheetStep(item: {__typename?: string; parentId?: string | null}): boolean {
  return (
    item.__typename === 'EntryLikeDocumentNode' && item.parentId != null && APIUtil.isWorksheet(item.parentId)
  );
}

export function buildBenchChipRow(item: BenchChipRowItem, referencedInText: string): RelevantRowData {
  return {
    id: item.id,
    name: item.name,
    referencedInText: plainReferenceText(referencedInText),
    modifiedAt: item.modifiedAt,
    location: getProjectFolderName(item),
    chip: <BenchChip id={item.id} size={ChipSize.SMALL} />,
  };
}

export function getFieldReferenceTaggedRows(
  items: Array<BenchChipRowItem>,
  label: string
): Array<RelevantRowData> {
  return items.map((item) => buildBenchChipRow(item, `${label} referenced in field`));
}

export function getWorksheetStepMentionRow(
  item: FilesMentioningRowItem,
  label: string
): RelevantRowData | null {
  if (!isWorksheetStep(item)) {
    return null;
  }
  return buildBenchChipRow(item, `${label} referenced in step '${item.name}'`);
}
export function getEntryMentionRow(item: FilesMentioningRowItem, label: string): RelevantRowData | null {
  if (!APIUtil.isEntry(item.id)) {
    return null;
  }
  return buildBenchChipRow(item, `${label} referenced in entry`);
}
export function getBiologicalEntityDescriptionMentionRow(
  item: FilesMentioningRowItem,
  label: string
): RelevantRowData | null {
  if (!APIUtil.isBiologicalEntity(item.id)) {
    return null;
  }
  return buildBenchChipRow(item, `${label} referenced in description`);
}
export function getAnyDescriptionMentionRow(item: FilesMentioningRowItem, label: string): RelevantRowData {
  return buildBenchChipRow(item, `${label} referenced in description`);
}
export function getTaskGroupsRows<
  TProcessOutput extends TaskGroupRowItem,
  TProcessTask extends TaskGroupRowItem,
>(
  processOutputs: Array<TProcessOutput>,
  processTasks: Array<TProcessTask>,
  label: string
): Array<RelevantRowData> {
  return [...processOutputs, ...processTasks].map((item) => {
    const {name, process} = item;
    const {modifiedAt, createdAt, editUrl, folder} = process;
    return {
      name,
      id: name,
      referencedInText: plainReferenceText(`${label} referenced in task group`),
      modifiedAt: modifiedAt ?? createdAt,
      location: folder?.name ?? null,
      chip: (
        <Chip
          text={name}
          icon="process"
          size={ChipSize.SMALL}
          className="chip-container"
          onClick={() => {
            navigateToURL(editUrl ?? '');
            analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CLICKED_RELEVANT_ITEM, {
              process_client_origin: process.processTaskSchema.clientOrigin,
            });
          }}
        />
      ),
    };
  });
}
